import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Stack } from 'ui/stack';

import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import { Loader } from 'ui/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import Goal from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal';
import GoalsManagerHeader from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal-manager-header/goal-manager-header';
import GoalsSidebar from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-sidebar/goals-sidebar';

import { getClientGoals } from 'api/generated/client-goals/client-goals';

import { useClient } from 'hooks/api/use-clients';
import { useCurrentUser } from 'hooks/api/use-current-user';

import {
  getLastPathSegment,
  getPathToGoal,
  getUrlParams,
} from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-manager.helper';

import type { Goal as GoalType } from 'containers/admin/clients/client/tabs-components/goals-tab/types';
import type { GoalSelectArg } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-sidebar/goals-sidebar';

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

const GoalsManager = (): React.JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [goals, setGoals] = useState<GoalType[]>([]);
  const [goalsCount, setGoalsCount] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedGoal, setSelectedGoal] = useState<string | number>('');
  const initialLoadComplete = useRef<boolean>(false);

  const params = useParams();
  const navigate = useNavigate();

  const { enteringClassYear, type, category, clientId, goalId } =
    getUrlParams(params);

  const user = useCurrentUser() as CurrentUserShape;
  const { data: client } = useClient(clientId);

  const isNew = goalId === 'new' || selectedGoal === 'new';

  const getGoals = useCallback(async (): Promise<void> => {
    if (
      !user?.data?.oktaUserId ||
      !clientId ||
      !category ||
      !type ||
      !enteringClassYear
    ) {
      return;
    }

    setLoading(true);

    try {
      const res = await getClientGoals(user.data.oktaUserId, Number(clientId), {
        'type.contains': type,
        'category.contains': category,
        'enteringClassYear.equals': Number(enteringClassYear),
        sort: ['createdDate,desc'],
      });

      const goalsData = (res.content ?? []).map(
        (row) => ({ ...row } as GoalType)
      );

      setGoals(goalsData);
      setGoalsCount(goalsData.length);
    } catch (error) {
      console.error('Error fetching goals', error);
    } finally {
      setLoading(false);
    }
  }, [category, clientId, enteringClassYear, type, user]);

  const handleGoalClick = useCallback(
    ({ event, to, id }: GoalSelectArg): void => {
      if (event) event.preventDefault();
      navigate(to);
      setSelectedGoal(id);
      if (id !== 'new') {
        getGoals();
      }
    },
    [navigate, getGoals]
  );

  const handleNewGoalClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    const currentUrl = getPathToGoal(window.location.pathname);

    const newPath = `${currentUrl}/new`;

    handleGoalClick({ event: e, to: newPath, id: 'new' });
  };

  useEffect(() => {
    if (goalId !== 'new' && Number.isNaN(Number(goalId))) {
      getGoals();
    }
  }, [goalId]);

  useEffect(() => {
    if (
      !initialLoadComplete.current &&
      clientId &&
      type &&
      category &&
      enteringClassYear
    ) {
      initialLoadComplete.current = true;
      getGoals();
    }
  }, [clientId, type, category, enteringClassYear]);

  useEffect(() => {
    if (goalsCount === null) {
      return;
    }

    if (goalsCount === 0 && goalId !== 'new') {
      navigate(`/admin/clients/${clientId}/goals/${enteringClassYear}`);

      return;
    }

    const goalIds = goals.map((goal) => goal.id);

    const currentGoalId = getLastPathSegment(window.location.pathname);
    const firstGoalId = goals[0]?.id;

    if (goalId !== 'new') {
      const newSelectedGoal = goalIds.includes(currentGoalId as never)
        ? (currentGoalId as string | number)
        : (firstGoalId as string | number);

      if (selectedGoal !== newSelectedGoal) {
        setSelectedGoal(newSelectedGoal);
      }
    }

    if (
      goalId !== 'new' &&
      !goalIds.includes(currentGoalId as never) &&
      firstGoalId
    ) {
      const currentUrl =
        goalId === 'new'
          ? getPathToGoal(window.location.pathname)
          : window.location.pathname;

      const newUrl = `${currentUrl}/${firstGoalId}`.replace('//', '/');

      navigate(newUrl);
    }
  }, [goalsCount, goals]);

  return (
    <ResponsiveWrapper customClass="h-full py-4">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Clients', href: '/admin/clients' },
          {
            label: client?.name ?? '',
            href: `/admin/clients/${clientId}`,
          },
          {
            label: 'Goals',
            href: `/admin/clients/${clientId}/goals/${enteringClassYear}`,
          },
        ]}
        customClass="mb-3"
      />
      <GoalsManagerHeader
        handleNewGoalClick={handleNewGoalClick}
        type={type}
        category={category}
        year={enteringClassYear}
        isCreation={isNew}
        isEdition={isEditMode}
        sx={{ mb: 2 }}
      />
      <Stack
        direction="row"
        sx={{ borderTopWidth: '1px', height: '100%', overflow: 'hidden' }}
      >
        <Loader loading={loading} overlay="white" sx={{ opacity: 0.5 }} />

        <GoalsSidebar
          goals={goals}
          handleGoalSelect={handleGoalClick}
          selectedGoal={selectedGoal}
          isNew={isNew}
          isEditMode={isEditMode}
        />

        <Goal
          data={goals.find((goal) => goal.id === selectedGoal)}
          isCreation={isNew}
          goalsLength={goals.length}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          getGoals={getGoals}
          selectedGoalId={selectedGoal}
          setLoading={setLoading}
          loading={loading}
        />
      </Stack>
    </ResponsiveWrapper>
  );
};

export default GoalsManager;
